// src/screens/PendingAMV.tsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Chip,
  CircularProgress,
  LinearProgress,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { apiFetch } from '../helpers';

interface PendingDeal {
  _id: string;
  fullAddress: string;
  state?: string | null;
  listingPrice?: number | null;
  scrapedAt?: string;
  updatedAt?: string;
  source: 'privy' | 'redfin';
  agentName?: string | null;
  agentPhone?: string | null;
  agentEmail?: string | null;
  redfinAmv?: number | null;
  realtorAmv?: number | null;
  amv?: number | null;
  amvSource?: string | null;
  isDeal?: boolean;
  dealStage?: string | null;
  redfinActive?: boolean | null;
  realtorActive?: boolean | null;
  listingActive?: boolean | null;
  realtorPropStatus?: string[] | null;
  bofaAmv?: number | null;
  amvError?: string | null;
}

interface ScraperStatus {
  mode: 'scrape' | 'amv' | 'unknown';
  addressesScrapedThisBatch: number;
  batchLimit: number;
  schedulerEnabled?: boolean;
}

interface PendingByState {
  _id: string;
  count: number;
}

const currency = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmt = (n?: number | null) => (typeof n === 'number' && n > 0 ? currency.format(n) : '—');

const sourceColors: Record<string, { bg: string; text: string }> = {
  privy: { bg: '#f5f3ff', text: '#7c3aed' },
};

type ViewMode = 'all' | 'waiting_bofa' | 'with_amv' | 'not_active' | 'redfin_queue' | 'no_amv_found';

export default function PendingAMV() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [tabCounts, setTabCounts] = useState({ all: 0, waitingBofa: 0, withAmv: 0, notActive: 0, redfinQueue: 0, noAmvFound: 0 });
  const [selectedState, setSelectedState] = useState<string>('');
  const [states, setStates] = useState<string[]>([]);
  const [tabProperties, setTabProperties] = useState<PendingDeal[]>([]);
  const [tabTotal, setTabTotal] = useState(0);
  const [scraperStatus, setScraperStatus] = useState<ScraperStatus>({
    mode: 'unknown',
    addressesScrapedThisBatch: 0,
    batchLimit: 500,
  });
  const [stats, setStats] = useState({ pendingAMV: 0, withAMV: 0, total: 0, pendingPrivy: 0, /* pendingRedfin: 0, */ donePrivy: 0, /* doneRedfin: 0, */ maxPerSource: 500 });
  const [pendingByState, setPendingByState] = useState<PendingByState[]>([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [clearing, setClearing] = useState(false);

  // Redfin AMV processing status
  const [redfinRunning, setRedfinRunning] = useState(false);
  const [redfinStatus, setRedfinStatus] = useState('');
  const [redfinProcessed, setRedfinProcessed] = useState(0);
  const [redfinSuccess, setRedfinSuccess] = useState(0);
  const [redfinFailed, setRedfinFailed] = useState(0);
  const [redfinLoading, setRedfinLoading] = useState(false);

  // Realtor AMV processing status
  const [realtorRunning, setRealtorRunning] = useState(false);
  const [realtorStatus, setRealtorStatus] = useState('');
  const [realtorProcessed, setRealtorProcessed] = useState(0);
  const [realtorSuccess, setRealtorSuccess] = useState(0);
  const [realtorFailed, setRealtorFailed] = useState(0);
  const [realtorLoading, setRealtorLoading] = useState(false);

  // BofA AMV processing status
  const [bofaRunning, setBofaRunning] = useState(false);
  const [bofaStatus, setBofaStatus] = useState('');
  const [bofaProcessed, setBofaProcessed] = useState(0);
  const [bofaSuccess, setBofaSuccess] = useState(0);
  const [bofaFailed, setBofaFailed] = useState(0);
  const [bofaDeals, setBofaDeals] = useState(0);
  const [bofaLoading, setBofaLoading] = useState(false);
  const [bofaStats, setBofaStats] = useState<any>(null);

  const loadData = useCallback(async () => {
    try {
      setError(null);
      // Legacy stats endpoint (kept for stats card, still referenced elsewhere)
      const [statsRes, countsRes] = await Promise.all([
        apiFetch('/api/scraped-deals/pending-amv'),
        apiFetch(`/api/scraped-deals/privy-tab-counts${selectedState ? `?state=${selectedState}` : ''}`),
      ]);
      const data = await statsRes.json();
      const countsData = await countsRes.json();

      if (data.ok) {
        setScraperStatus(data.scraperStatus || { mode: 'unknown', addressesScrapedThisBatch: 0, batchLimit: 500 });
        setStats(data.stats || { pendingAMV: 0, withAMV: 0, total: 0, pendingPrivy: 0, donePrivy: 0, maxPerSource: 500 });
        setPendingByState(data.pendingByState || []);
      }
      if (countsData.ok) {
        setTabCounts(countsData.counts);
      }
      if (!data.ok && !countsData.ok) {
        setError(data.error || countsData.error || 'Failed to load data');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedState]);

  // Load tab-specific data based on viewMode
  // silent=true skips the loading spinner (used for background refreshes)
  const loadTabData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);

      const endpoint = `/api/scraped-deals/privy-tab?tab=${viewMode}&limit=200${selectedState ? `&state=${selectedState}` : ''}`;

      const res = await apiFetch(endpoint);
      const data = await res.json();

      const records = data.properties || data.records || [];
      setTabProperties(records);
      setTabTotal(data.total || records.length || 0);

      // Collect unique states for the filter dropdown
      const props = records;
      if (!selectedState && props.length > 0) {
        const uniqueStates = [...new Set(props.map((p: PendingDeal) => p.state).filter(Boolean))] as string[];
        setStates(prev => prev.length > 0 ? prev : uniqueStates.sort());
      }
    } catch (err: any) {
      if (!silent) setError(err?.message || 'Failed to load tab data');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [viewMode, selectedState]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  // Auto-refresh every 5 seconds when enabled (silent — no spinner)
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      loadData();
      loadTabData(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadData, loadTabData]);

  const progressPercent = scraperStatus.batchLimit > 0
    ? Math.min(100, (scraperStatus.addressesScrapedThisBatch / scraperStatus.batchLimit) * 100)
    : 0;

  const modeColor = scraperStatus.mode === 'scrape' ? '#22c55e' : scraperStatus.mode === 'amv' ? '#3b82f6' : '#9ca3af';
  const modeLabel = scraperStatus.mode === 'scrape' ? 'SCRAPING' : scraperStatus.mode === 'amv' ? 'FETCHING AMV' : 'UNKNOWN';

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to clear ALL data and start fresh? This cannot be undone.')) {
      return;
    }
    setClearing(true);
    try {
      const res = await apiFetch('/api/scraped-deals/clear-all', { method: 'POST' });
      const data = await res.json();
      if (data.ok) {
        alert(`Cleared ${data.cleared?.scrapedDeals || 0} addresses. Ready to start fresh!`);
        loadData();
      } else {
        alert('Failed to clear: ' + (data.error || 'Unknown error'));
      }
    } catch (err: any) {
      alert('Failed to clear: ' + (err?.message || 'Unknown error'));
    } finally {
      setClearing(false);
    }
  };

  // Fetch Redfin AMV status
  const fetchRedfinStatus = useCallback(async () => {
    try {
      const res = await apiFetch('/api/scraped-redfin-amv/status');
      const data = await res.json();
      setRedfinRunning(data.isRunning || false);
      setRedfinStatus(data.currentStatus || '');
      setRedfinProcessed(data.privyProcessed || 0);
      setRedfinSuccess(data.privySuccess || 0);
      setRedfinFailed(data.privyFailed || 0);
    } catch {
      // Ignore errors
    }
  }, []);

  // Poll Redfin AMV status
  useEffect(() => {
    fetchRedfinStatus();
    const interval = setInterval(fetchRedfinStatus, 3000);
    return () => clearInterval(interval);
  }, [fetchRedfinStatus]);

  // Start Redfin AMV loop
  const handleStartRedfin = async () => {
    setRedfinLoading(true);
    try {
      const res = await apiFetch('/api/scraped-redfin-amv/start', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setRedfinRunning(true);
        fetchRedfinStatus();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to start Redfin AMV');
    }
    setRedfinLoading(false);
  };

  // Stop Redfin AMV loop
  const handleStopRedfin = async () => {
    setRedfinLoading(true);
    try {
      const res = await apiFetch('/api/scraped-redfin-amv/stop', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        fetchRedfinStatus();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to stop Redfin AMV');
    }
    setRedfinLoading(false);
  };

  // Fetch Realtor AMV status
  const fetchRealtorStatus = useCallback(async () => {
    try {
      const res = await apiFetch('/api/scraped-realtor-amv/status');
      const data = await res.json();
      setRealtorRunning(data.isRunning || false);
      setRealtorStatus(data.currentStatus || '');
      setRealtorProcessed(data.privyProcessed || 0);
      setRealtorSuccess(data.privySuccess || 0);
      setRealtorFailed(data.privyFailed || 0);
    } catch {
      // Ignore errors
    }
  }, []);

  // Poll Realtor AMV status
  useEffect(() => {
    fetchRealtorStatus();
    const interval = setInterval(fetchRealtorStatus, 3000);
    return () => clearInterval(interval);
  }, [fetchRealtorStatus]);

  // Start Realtor AMV loop
  const handleStartRealtor = async () => {
    setRealtorLoading(true);
    try {
      const res = await apiFetch('/api/scraped-realtor-amv/start', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setRealtorRunning(true);
        fetchRealtorStatus();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to start Realtor AMV');
    }
    setRealtorLoading(false);
  };

  // Stop Realtor AMV loop
  const handleStopRealtor = async () => {
    setRealtorLoading(true);
    try {
      const res = await apiFetch('/api/scraped-realtor-amv/stop', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        fetchRealtorStatus();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to stop Realtor AMV');
    }
    setRealtorLoading(false);
  };

  // Fetch BofA AMV status
  const fetchBofaStatus = useCallback(async () => {
    try {
      const res = await apiFetch('/api/scraped-redfin-amv/bofa/status');
      const data = await res.json();
      setBofaRunning(data.isProcessing || false);
      setBofaStatus(data.currentStatus || '');
      setBofaProcessed(data.processedCount || 0);
      setBofaSuccess(data.successCount || 0);
      setBofaFailed(data.failedCount || 0);
      setBofaDeals(data.movedToDeals || 0);
      if (data.stats) setBofaStats(data.stats);
    } catch {
      // Ignore errors
    }
  }, []);

  // Poll BofA status
  useEffect(() => {
    fetchBofaStatus();
    const interval = setInterval(fetchBofaStatus, 5000);
    return () => clearInterval(interval);
  }, [fetchBofaStatus]);

  // Start BofA loop
  const handleStartBofa = async () => {
    setBofaLoading(true);
    try {
      const res = await apiFetch('/api/scraped-redfin-amv/bofa/start', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setBofaRunning(true);
        fetchBofaStatus();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to start BofA');
    }
    setBofaLoading(false);
  };

  // Stop BofA loop
  const handleStopBofa = async () => {
    setBofaLoading(true);
    try {
      const res = await apiFetch('/api/scraped-redfin-amv/bofa/stop', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        fetchBofaStatus();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to stop BofA');
    }
    setBofaLoading(false);
  };

  const handleCleanupInvalid = async () => {
    if (!window.confirm('This will remove addresses that look like descriptions (not real addresses). Continue?')) {
      return;
    }
    setClearing(true);
    try {
      const res = await apiFetch('/api/scraped-deals/cleanup-invalid', { method: 'POST' });
      const data = await res.json();
      if (data.ok) {
        alert(`Removed ${data.deleted} invalid addresses. ${data.after} valid addresses remain.`);
        loadData();
      } else {
        alert('Failed to cleanup: ' + (data.error || 'Unknown error'));
      }
    } catch (err: any) {
      alert('Failed to cleanup: ' + (err?.message || 'Unknown error'));
    } finally {
      setClearing(false);
    }
  };

  return (
    <div style={{ padding: 24, background: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111' }}>Pending AMV Queue (Privy)</h2>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            onClick={handleCleanupInvalid}
            disabled={clearing}
            sx={{ textTransform: 'none' }}
          >
            {clearing ? 'Cleaning...' : 'Cleanup Invalid'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={handleClearAll}
            disabled={clearing}
            sx={{ textTransform: 'none' }}
          >
            {clearing ? 'Clearing...' : 'Clear All & Start Fresh'}
          </Button>
          <Button
            variant={autoRefresh ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setAutoRefresh(!autoRefresh)}
            sx={{
              textTransform: 'none',
              backgroundColor: autoRefresh ? '#22c55e' : undefined,
              '&:hover': { backgroundColor: autoRefresh ? '#16a34a' : undefined },
            }}
          >
            {autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh OFF'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={loadData}
            sx={{ textTransform: 'none' }}
          >
            Refresh Now
          </Button>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      {/* Scraper Status Card — COMMENTED OUT */}
      {false && <div
        style={{
          background: '#fff',
          border: `2px solid ${modeColor}`,
          borderRadius: 12,
          padding: 20,
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div
            style={{
              background: modeColor,
              color: '#fff',
              padding: '6px 16px',
              borderRadius: 20,
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            {modeLabel}
          </div>
          <div style={{ fontSize: 14, color: '#6b7280' }}>
            Current scheduler mode
          </div>
        </div>

        {scraperStatus.mode === 'scrape' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: '#111' }}>
                Batch Progress: {scraperStatus.addressesScrapedThisBatch} / {scraperStatus.batchLimit}
              </span>
              <span style={{ color: '#6b7280' }}>
                {progressPercent.toFixed(1)}%
              </span>
            </div>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: '#e5e7eb',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#22c55e',
                  borderRadius: 5,
                },
              }}
            />
            <div style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
              {scraperStatus.batchLimit - scraperStatus.addressesScrapedThisBatch} addresses remaining before switching to AMV mode
            </div>
          </div>
        )}

        {scraperStatus.mode === 'amv' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CircularProgress size={24} sx={{ color: '#3b82f6' }} />
            <span style={{ fontWeight: 600, color: '#111' }}>
              Processing {stats.pendingAMV} addresses for BofA AMV...
            </span>
          </div>
        )}
      </div>}

      {/* Redfin AMV Processing Status — COMMENTED OUT */}
      {false && <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          marginBottom: 16,
          background: redfinRunning ? '#dbeafe' : '#f3f4f6',
          borderRadius: 8,
          border: `1px solid ${redfinRunning ? '#93c5fd' : '#e5e7eb'}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: redfinRunning ? '#2563eb' : '#9ca3af',
              animation: redfinRunning ? 'pulse 1s infinite' : 'none',
            }}
          />
          <span style={{ fontWeight: 600, color: '#111' }}>
            Redfin Active Check:
          </span>
          <span style={{ color: redfinRunning ? '#1d4ed8' : '#6b7280' }}>
            {redfinRunning ? 'Running' : 'Stopped'}
          </span>
          {redfinProcessed > 0 && (
            <>
              <span style={{ color: '#059669', fontWeight: 600 }}>
                {redfinSuccess} active
              </span>
              <span style={{ color: '#dc2626', fontWeight: 600 }}>
                {redfinFailed} not found
              </span>
              <span style={{ color: '#6b7280' }}>
                ({redfinProcessed} total)
              </span>
            </>
          )}
          {redfinStatus && (
            <span style={{ color: '#4b5563', fontSize: 13, maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {redfinStatus}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!redfinRunning ? (
            <Button
              variant="contained"
              size="small"
              onClick={handleStartRedfin}
              disabled={redfinLoading}
              sx={{
                textTransform: 'none',
                backgroundColor: '#2563eb',
                '&:hover': { backgroundColor: '#1d4ed8' },
              }}
            >
              {redfinLoading ? 'Starting...' : 'Start Redfin Check'}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={handleStopRedfin}
              disabled={redfinLoading}
              sx={{
                textTransform: 'none',
                backgroundColor: '#ef4444',
                '&:hover': { backgroundColor: '#dc2626' },
              }}
            >
              {redfinLoading ? 'Stopping...' : 'Stop Redfin Check'}
            </Button>
          )}
        </div>
      </div>}

      {/* Realtor AMV Processing Status — COMMENTED OUT */}
      {false && <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          marginBottom: 16,
          background: realtorRunning ? '#fef3c7' : '#f3f4f6',
          borderRadius: 8,
          border: `1px solid ${realtorRunning ? '#fcd34d' : '#e5e7eb'}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: realtorRunning ? '#f59e0b' : '#9ca3af',
              animation: realtorRunning ? 'pulse 1s infinite' : 'none',
            }}
          />
          <span style={{ fontWeight: 600, color: '#111' }}>
            Realtor Active Check:
          </span>
          <span style={{ color: realtorRunning ? '#b45309' : '#6b7280' }}>
            {realtorRunning ? 'Running' : 'Stopped'}
          </span>
          {realtorProcessed > 0 && (
            <>
              <span style={{ color: '#059669', fontWeight: 600 }}>
                {realtorSuccess} active
              </span>
              <span style={{ color: '#dc2626', fontWeight: 600 }}>
                {realtorFailed} not active
              </span>
              <span style={{ color: '#6b7280' }}>
                ({realtorProcessed} total)
              </span>
            </>
          )}
          {realtorStatus && (
            <span style={{ color: '#4b5563', fontSize: 13, maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {realtorStatus}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!realtorRunning ? (
            <Button
              variant="contained"
              size="small"
              onClick={handleStartRealtor}
              disabled={realtorLoading}
              sx={{
                textTransform: 'none',
                backgroundColor: '#f59e0b',
                '&:hover': { backgroundColor: '#d97706' },
              }}
            >
              {realtorLoading ? 'Starting...' : 'Start Realtor Check'}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={handleStopRealtor}
              disabled={realtorLoading}
              sx={{
                textTransform: 'none',
                backgroundColor: '#ef4444',
                '&:hover': { backgroundColor: '#dc2626' },
              }}
            >
              {realtorLoading ? 'Stopping...' : 'Stop Realtor Check'}
            </Button>
          )}
        </div>
      </div>}

      {/* BofA AMV Processing Status — COMMENTED OUT */}
      {false && <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          marginBottom: 16,
          background: bofaRunning ? '#f3e8ff' : '#f3f4f6',
          borderRadius: 8,
          border: `1px solid ${bofaRunning ? '#c084fc' : '#e5e7eb'}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: bofaRunning ? '#7c3aed' : '#9ca3af',
              animation: bofaRunning ? 'pulse 1s infinite' : 'none',
            }}
          />
          <span style={{ fontWeight: 600, color: '#111' }}>
            BofA AMV (4 browsers):
          </span>
          <span style={{ color: bofaRunning ? '#6d28d9' : '#6b7280' }}>
            {bofaRunning ? 'Running' : 'Stopped'}
          </span>
          {bofaProcessed > 0 && (
            <>
              <span style={{ color: '#059669', fontWeight: 600 }}>
                {bofaSuccess} found
              </span>
              <span style={{ color: '#dc2626', fontWeight: 600 }}>
                {bofaFailed} failed
              </span>
              <span style={{ color: '#7c3aed', fontWeight: 600 }}>
                {bofaDeals} deals
              </span>
              <span style={{ color: '#6b7280' }}>
                ({bofaProcessed} total)
              </span>
            </>
          )}
          {bofaStats && (
            <span style={{ color: '#6b7280', fontSize: 13 }}>
              Queue: {bofaStats.bofaPending || 0}
            </span>
          )}
          {bofaStatus && (
            <span style={{ color: '#4b5563', fontSize: 13, maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {bofaStatus}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!bofaRunning ? (
            <Button
              variant="contained"
              size="small"
              onClick={handleStartBofa}
              disabled={bofaLoading}
              sx={{
                textTransform: 'none',
                backgroundColor: '#7c3aed',
                '&:hover': { backgroundColor: '#6d28d9' },
              }}
            >
              {bofaLoading ? 'Starting...' : 'Start BofA AMV'}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={handleStopBofa}
              disabled={bofaLoading}
              sx={{
                textTransform: 'none',
                backgroundColor: '#ef4444',
                '&:hover': { backgroundColor: '#dc2626' },
              }}
            >
              {bofaLoading ? 'Stopping...' : 'Stop BofA AMV'}
            </Button>
          )}
        </div>
      </div>}

      {/* Stats Cards — COMMENTED OUT */}
      {false && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Card title="Pending AMV" value={stats.pendingAMV} color="#f59e0b" highlight />
        <Card title="With AMV" value={stats.withAMV} color="#22c55e" />
        <Card title="Total Scraped" value={stats.total} />
        <Card title="Batch Scraped" value={scraperStatus.addressesScrapedThisBatch} color="#3b82f6" />
      </div>}

      {/* View Mode Toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => newMode && setViewMode(newMode)}
          size="small"
        >
          <ToggleButton value="all" sx={{ textTransform: 'none', fontWeight: 600 }}>
            All Properties ({tabCounts.all.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="waiting_bofa" sx={{ textTransform: 'none', fontWeight: 600, color: viewMode === 'waiting_bofa' ? '#7c3aed' : undefined }}>
            Waiting for BofA ({tabCounts.waitingBofa.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="with_amv" sx={{ textTransform: 'none', fontWeight: 600, color: viewMode === 'with_amv' ? '#166534' : undefined }}>
            With AMV value ({tabCounts.withAmv.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="not_active" sx={{ textTransform: 'none', fontWeight: 600, color: viewMode === 'not_active' ? '#dc2626' : undefined }}>
            Not Active ({tabCounts.notActive.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="redfin_queue" sx={{ textTransform: 'none', fontWeight: 600, color: viewMode === 'redfin_queue' ? '#dc2626' : undefined }}>
            Redfin Queue ({tabCounts.redfinQueue.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="no_amv_found" sx={{ textTransform: 'none', fontWeight: 600, color: viewMode === 'no_amv_found' ? '#b45309' : undefined }}>
            No AMV Found ({tabCounts.noAmvFound.toLocaleString()})
          </ToggleButton>
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>State</InputLabel>
          <Select
            value={selectedState}
            label="State"
            onChange={(e) => setSelectedState(e.target.value)}
          >
            <MenuItem value="">All States</MenuItem>
            {states.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {/* Pending by State — COMMENTED OUT */}
      {false && pendingByState.length > 0 && (
        <div
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
          }}
        >
          <div style={{ fontWeight: 700, color: '#111', marginBottom: 12 }}>
            Pending by State
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {pendingByState.map((s) => (
              <Chip
                key={s._id || 'unknown'}
                label={`${s._id || 'Unknown'}: ${s.count}`}
                sx={{
                  backgroundColor: '#f3f4f6',
                  fontWeight: 600,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, marginBottom: 16, color: '#dc2626' }}>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress />
        </div>
      )}

      {/* Properties Table — driven by viewMode */}
      {!loading && (
        <div
          style={{
            overflowX: 'auto',
            borderRadius: 12,
            border: '1px solid #e5e7eb',
            background: '#fff',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, color: '#111' }}>
              {viewMode === 'all' ? 'All Properties' :
               viewMode === 'waiting_bofa' ? 'Waiting for BofA' :
               viewMode === 'with_amv' ? 'With AMV value' :
               viewMode === 'not_active' ? 'Not Active' :
               viewMode === 'redfin_queue' ? 'Redfin Queue' :
               'No AMV Found'} ({tabTotal.toLocaleString()})
            </span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr style={{ background: '#111827', color: '#fff' }}>
                {(() => {
                  const isPending = viewMode === 'waiting_bofa' || viewMode === 'redfin_queue' || viewMode === 'no_amv_found';
                const isBofaResults = viewMode === 'with_amv';
                  const isActiveTab = false;
                  const isNotActiveTab = viewMode === 'not_active';
                  const headers = isPending
                    ? ['Address', 'State', 'Listing Price', 'Source', 'Status', 'Scraped At']
                    : isNotActiveTab
                    ? ['Address', 'State', 'Listing Price', 'Realtor Status', 'Redfin', 'Updated At']
                    : ['Address', 'State', 'Listing Price', 'AMV', 'AMV Source', 'Deal?', 'Updated At'];
                  return headers.map((h, i) => (
                    <th
                      key={h}
                      style={{
                        textAlign: i === 0 ? 'left' : 'right',
                        padding: '12px 14px',
                        fontSize: 12,
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ));
                })()}
              </tr>
            </thead>
            <tbody>
              {tabProperties.map((deal, i) => {
                const zebra = i % 2 === 0 ? '#ffffff' : '#f9fafb';
                const isPending = viewMode === 'waiting_bofa' || viewMode === 'redfin_queue' || viewMode === 'no_amv_found';
                const isBofaResults = viewMode === 'with_amv';
                const sourceStyle = sourceColors[deal.source] || { bg: '#f3f4f6', text: '#374151' };
                const bestAmv = deal.redfinAmv || deal.realtorAmv || deal.amv;

                if (isPending) {
                  return (
                    <tr key={deal._id} style={{ background: zebra }}>
                      <td style={{ padding: 14, minWidth: 280 }}>
                        <div style={{ fontWeight: 600, color: '#111' }}>{deal.fullAddress || '—'}</div>
                      </td>
                      <td style={{ padding: 14, textAlign: 'right', color: '#6b7280' }}>{deal.state || '—'}</td>
                      <td style={{ padding: 14, textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(deal.listingPrice)}</td>
                      <td style={{ padding: 14, textAlign: 'right' }}>
                        <Chip
                          label={deal.source || '—'}
                          size="small"
                          sx={{ backgroundColor: sourceStyle.bg, color: sourceStyle.text, fontWeight: 600 }}
                        />
                      </td>
                      <td style={{ padding: 14, textAlign: 'right' }}>
                        <Chip
                          label={viewMode === 'waiting_bofa' ? 'BofA Pending' : 'No BofA Data'}
                          size="small"
                          sx={{
                            backgroundColor: viewMode === 'waiting_bofa' ? '#f3e8ff' : '#fee2e2',
                            color: viewMode === 'waiting_bofa' ? '#7c3aed' : '#b91c1c',
                            fontWeight: 600,
                          }}
                        />
                      </td>
                      <td style={{ padding: 14, textAlign: 'right', color: '#6b7280', fontSize: 13 }}>
                        {deal.scrapedAt ? new Date(deal.scrapedAt).toLocaleString() : '—'}
                      </td>
                    </tr>
                  );
                }

                // Active Listings tab — REMOVED in 5-tab layout, left for safety (never triggers)
                if (false) {
                  const activeSource = deal.redfinActive && deal.realtorActive ? 'Both'
                    : deal.redfinActive ? 'Redfin'
                    : deal.realtorActive ? 'Realtor'
                    : 'Unknown';
                  return (
                    <tr key={deal._id} style={{ background: zebra }}>
                      <td style={{ padding: 14, minWidth: 280 }}>
                        <div style={{ fontWeight: 600, color: '#111' }}>{deal.fullAddress || '—'}</div>
                      </td>
                      <td style={{ padding: 14, textAlign: 'right', color: '#6b7280' }}>{deal.state || '—'}</td>
                      <td style={{ padding: 14, textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(deal.listingPrice)}</td>
                      <td style={{ padding: 14, textAlign: 'right', color: '#7c3aed', fontWeight: 600 }}>{fmt(bestAmv)}</td>
                      <td style={{ padding: 14, textAlign: 'right' }}>
                        <Chip
                          label={activeSource}
                          size="small"
                          sx={{
                            backgroundColor: '#dcfce7',
                            color: '#166534',
                            fontWeight: 600,
                          }}
                        />
                      </td>
                      <td style={{ padding: 14, textAlign: 'right' }}>
                        <Chip
                          label={deal.isDeal ? 'DEAL' : 'No'}
                          size="small"
                          sx={{
                            backgroundColor: deal.isDeal ? '#dcfce7' : '#f3f4f6',
                            color: deal.isDeal ? '#166534' : '#6b7280',
                            fontWeight: 600,
                          }}
                        />
                      </td>
                      <td style={{ padding: 14, textAlign: 'right', color: '#6b7280', fontSize: 13 }}>
                        {(deal.updatedAt || deal.scrapedAt) ? new Date(deal.updatedAt || deal.scrapedAt!).toLocaleString() : '—'}
                      </td>
                    </tr>
                  );
                }

                // Not Active tab
                if (viewMode === 'not_active') {
                  const realtorStatusText = deal.realtorPropStatus?.length
                    ? deal.realtorPropStatus.join(', ')
                    : 'not found';
                  return (
                    <tr key={deal._id} style={{ background: zebra }}>
                      <td style={{ padding: 14, minWidth: 280 }}>
                        <div style={{ fontWeight: 600, color: '#111' }}>{deal.fullAddress || '—'}</div>
                      </td>
                      <td style={{ padding: 14, textAlign: 'right', color: '#6b7280' }}>{deal.state || '—'}</td>
                      <td style={{ padding: 14, textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(deal.listingPrice)}</td>
                      <td style={{ padding: 14, textAlign: 'right' }}>
                        <Chip
                          label={realtorStatusText}
                          size="small"
                          sx={{
                            backgroundColor: '#fef2f2',
                            color: '#dc2626',
                            fontWeight: 600,
                          }}
                        />
                      </td>
                      <td style={{ padding: 14, textAlign: 'right' }}>
                        <Chip
                          label={deal.redfinActive === false ? 'Not Found' : '—'}
                          size="small"
                          sx={{
                            backgroundColor: '#f3f4f6',
                            color: '#6b7280',
                            fontWeight: 600,
                          }}
                        />
                      </td>
                      <td style={{ padding: 14, textAlign: 'right', color: '#6b7280', fontSize: 13 }}>
                        {(deal.updatedAt || deal.scrapedAt) ? new Date(deal.updatedAt || deal.scrapedAt!).toLocaleString() : '—'}
                      </td>
                    </tr>
                  );
                }

                // Non-pending views: with_redfin, with_realtor, all
                return (
                  <tr key={deal._id} style={{ background: zebra }}>
                    <td style={{ padding: 14, minWidth: 280 }}>
                      <div style={{ fontWeight: 600, color: '#111' }}>{deal.fullAddress || '—'}</div>
                    </td>
                    <td style={{ padding: 14, textAlign: 'right', color: '#6b7280' }}>{deal.state || '—'}</td>
                    <td style={{ padding: 14, textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(deal.listingPrice)}</td>
                    <td style={{ padding: 14, textAlign: 'right', color: '#7c3aed', fontWeight: 600 }}>{fmt(bestAmv)}</td>
                    <td style={{ padding: 14, textAlign: 'right' }}>
                      {viewMode === 'with_amv' ? (
                        <span style={{ fontSize: 13 }}>
                          {deal.bofaAmv && deal.bofaAmv > 0
                            ? fmt(deal.bofaAmv)
                            : deal.amvError === 'bofa_no_data'
                            ? 'No Data'
                            : deal.amvError
                            ? 'Failed'
                            : '—'}
                        </span>
                      ) : (
                        <Chip
                          label={deal.amvSource || '—'}
                          size="small"
                          sx={{
                            backgroundColor: deal.amvSource === 'redfin' ? '#dbeafe' : deal.amvSource === 'realtor' ? '#fef3c7' : deal.amvSource === 'bofa' ? '#f3e8ff' : '#f3f4f6',
                            color: deal.amvSource === 'redfin' ? '#2563eb' : deal.amvSource === 'realtor' ? '#b45309' : deal.amvSource === 'bofa' ? '#7c3aed' : '#6b7280',
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </td>
                    <td style={{ padding: 14, textAlign: 'right' }}>
                      <Chip
                        label={deal.isDeal ? 'DEAL' : 'No'}
                        size="small"
                        sx={{
                          backgroundColor: deal.isDeal ? '#dcfce7' : '#f3f4f6',
                          color: deal.isDeal ? '#166534' : '#6b7280',
                          fontWeight: 600,
                        }}
                      />
                    </td>
                    <td style={{ padding: 14, textAlign: 'right', color: '#6b7280', fontSize: 13 }}>
                      {(deal.updatedAt || deal.scrapedAt) ? new Date(deal.updatedAt || deal.scrapedAt!).toLocaleString() : '—'}
                    </td>
                  </tr>
                );
              })}
              {tabProperties.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                    {viewMode === 'all' ? 'No Privy properties found.' :
                     viewMode === 'waiting_bofa' ? 'No properties waiting for BofA. All caught up!' :
                     viewMode === 'with_amv' ? 'No properties with AMV values yet.' :
                     viewMode === 'redfin_queue' ? 'Redfin queue is empty — nothing waiting for Redfin fallback.' :
                     viewMode === 'no_amv_found' ? 'Nothing here — every property got an AMV from BofA or Redfin.' :
                     'No not-active properties yet. Properties not for sale on both Redfin and Realtor will appear here.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Info Box */}
      <div
        style={{
          marginTop: 16,
          padding: 16,
          background: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: 8,
          color: '#0369a1',
          fontSize: 14,
        }}
      >
        <strong>How it works:</strong>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
          <li>The scraper fetches addresses from Privy (SCRAPE mode)</li>
          <li>Pipeline: <strong>Redfin (active check) &rarr; Realtor (active check) &rarr; BofA AMV (4 browsers)</strong></li>
          <li>Redfin checks if the property is listed on Redfin GIS (free, no proxy)</li>
          <li>Realtor checks <code>prop_status</code> from autocomplete API (free, no proxy)</li>
          <li>If EITHER Redfin or Realtor confirms active &rarr; sent to BofA for AMV lookup (paid proxy)</li>
          <li>If BOTH say not active &rarr; property stops at <strong>Not Active</strong> (saves proxy cost)</li>
          <li>BofA finds AMV &rarr; Listing Price &le; 55% of AMV (and AMV &gt; $200k) = Deal</li>
        </ul>
      </div>
    </div>
  );
}

function Card({ title, value, color, highlight }: { title: string; value: number | string; color?: string; highlight?: boolean }) {
  return (
    <div
      style={{
        border: highlight ? `2px solid ${color || '#f59e0b'}` : '1px solid #e5e7eb',
        borderRadius: 10,
        padding: 16,
        background: highlight ? '#fffbeb' : '#fff',
      }}
    >
      <div style={{ fontSize: 12, color: '#6b7280' }}>{title}</div>
      <div style={{ fontSize: 28, fontWeight: 800, marginTop: 4, color: color || '#111' }}>{value}</div>
    </div>
  );
}
